import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { SITE } from "../lib/site";
import iconAsset from "../assets/favicon.png.asset.json";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          O artigo que você procura não existe ou foi movido do arquivo.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-accent">Voltar para a capa</Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo deu errado. Você pode tentar novamente ou voltar para a capa.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
          <a href="/" className="btn-ghost">Ir para a capa</a>
        </div>
      </div>
    </div>
  );
}

const ROOT_TITLE = "Liderança com Valores — Conteúdo para líderes e empresas";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: ROOT_TITLE },
      { name: "description", content: SITE.description },
      { name: "author", content: SITE.name },
      { name: "theme-color", content: "#FBBF24" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: ROOT_TITLE },
      { name: "twitter:title", content: ROOT_TITLE },
      { property: "og:description", content: SITE.description },
      { name: "twitter:description", content: SITE.description },
      { title: "Liderança com Valores — Conteúdo para líderes e empresas" },
      { property: "og:title", content: "Liderança com Valores — Conteúdo para líderes e empresas" },
      { name: "twitter:title", content: "Liderança com Valores — Conteúdo para líderes e empresas" },
      { name: "description", content: "Artigos, análises e guias sobre liderança, gestão, cultura organizacional, estratégia e empreendedorismo." },
      { property: "og:description", content: "Artigos, análises e guias sobre liderança, gestão, cultura organizacional, estratégia e empreendedorismo." },
      { name: "twitter:description", content: "Artigos, análises e guias sobre liderança, gestão, cultura organizacional, estratégia e empreendedorismo." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/b2c029fb-f599-4326-8c7e-5dec73ef1159" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/b2c029fb-f599-4326-8c7e-5dec73ef1159" },
    ],
    links: [
      { rel: "icon", href: iconAsset.url, type: "image/png" },
      { rel: "apple-touch-icon", href: iconAsset.url },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=G-WT7Z5FZ72H",
        type: "text/javascript",
      },
      {
        type: "text/javascript",
        children: `window.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag('js', new Date());\ngtag('config', 'G-WT7Z5FZ72H');`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NewsMediaOrganization",
              "@id": "/#organization",
              name: SITE.name,
              logo: iconAsset.url,
              description: SITE.description,
              email: SITE.email,
            },
            {
              "@type": "WebSite",
              "@id": "/#website",
              name: SITE.name,
              description: SITE.description,
              inLanguage: "pt-BR",
              publisher: { "@id": "/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1"><Outlet /></main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </QueryClientProvider>
  );
}
